import * as React from 'react'

import {
  Box,
  Button,
  Checkbox,
  CounterBadge,
  Icon,
  Text,
  Touchable,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { AtsQuestionFragment } from 'generated/resources/graphql'
import { filter, isEmpty, pickBy, xor } from 'lodash'

import Popover from 'modules/core/components/Popover'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { useQuestions } from 'modules/ats/hooks/questions/useQuestions'

type Answers = Record<number, string>

interface Props {
  jobId: number
  value: Answers
  onChange: (newValue: Answers) => void
}

const AnswerInput = ({
  question,
  value,
  onChange,
}: {
  question: AtsQuestionFragment
  value?: string
  onChange: (newValue: string) => void
}) => {
  if (['text', 'long_text'].includes(question.questionType)) {
    return (
      <TextField
        label={question.label}
        placeholder={i18n.t('ats.applications_table.filters.text_placeholder')}
        value={value || ''}
        onChange={onChange}
      />
    )
  }

  if (question.questionType === 'multiple_choice') {
    const values = JSON.parse(value || '[]')
    return (
      <Box justifyContent="flexStart" textAlign="left" gap="s8">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {question.label}
        </Text.Normal>
        <Box gap="s4" paddingLeft="s8">
          {question.options?.map((option) => (
            <Checkbox
              key={option.text}
              label={option.text}
              value={option.text}
              onChange={() => {
                const newValues = xor(values, [option.text])
                return onChange(newValues.length ? JSON.stringify(newValues) : '')
              }}
              checked={values.includes(option.text)}
            />
          ))}
        </Box>
      </Box>
    )
  }

  if (question.questionType === 'single_choice') {
    return (
      <Select
        label={question.label}
        options={
          question.options?.map((option) => ({ label: option.text, value: option.text })) || []
        }
        defaultOption={{ value: '', label: i18n.t('ats.applications_table.filters.select_option') }}
        value={value || ''}
        onChange={onChange}
      />
    )
  }

  if (question.questionType === 'file') {
    return null
  }

  return null
}

export const AnswersFilter = ({ jobId, value, onChange }: Props) => {
  const [filters, setFilters] = React.useState<Answers>({})
  const [open, toggle] = useToggle(false)
  const { questions } = useQuestions({ atsJobPostingIds: [jobId] })

  const activeFiltersCount = filter(value, (value, _key) => !isEmpty(value)).length

  const onApplyFilters = () => {
    const activeFilters = pickBy(filters, (value, _key) => !isEmpty(value))
    onChange(activeFilters)
    toggle(false)()
  }

  const resetFilters = () => {
    setFilters({})
    onChange({})
  }

  if (!questions.length) {
    return null
  }

  return (
    <Popover open={open} toggle={toggle} attachment="top center" size="huge">
      <Touchable disabled={questions.length === 0}>
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
          background="grey000White"
          border={{
            all: {
              width: 's2',
              style: 'solid',
              color: 'grey400',
            },
          }}
          borderRadius={{ all: 'abs008' }}
          padding="s8"
          gap="s8"
        >
          <CounterBadge count={activeFiltersCount} noCountIcon="filter" />
          <Text.Normal size="300" color="grey1000">
            {i18n.t('ats.applications_table.filters.answers')}
          </Text.Normal>
          <Icon.Small icon={open ? 'caret-up' : 'caret-down'} />
        </Box>
      </Touchable>

      <Box>
        <Box padding="s24" maxHeight="s600" overflowY="auto">
          <Box
            borderRadius={{ all: 'abs004' }}
            border={{
              all: {
                width: 's1',
                style: 'solid',
                color: 'grey400',
              },
            }}
          >
            <Box
              flexDirection="row"
              alignItems="center"
              gap="s8"
              padding="s8"
              border={{
                bottom: {
                  width: 's1',
                  style: 'solid',
                  color: 'grey400',
                },
              }}
            >
              <Icon.Medium icon="question-circle-line" color="grey800" />
              <Text.Normal color="grey800">
                {i18n.t('ats.applications_table.filters.answers')}
              </Text.Normal>
            </Box>
            <Box gap="s8" padding="s8" background="grey200">
              {questions.map((question) => (
                <AnswerInput
                  key={question.id}
                  question={question}
                  value={filters[question.id]}
                  onChange={(newValue) => {
                    setFilters({ ...filters, [question.id]: newValue })
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          background="grey200"
          flexDirection="row"
          justifyContent="flexEnd"
          padding="s16"
          gap="s8"
          border={{
            top: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Button type="secondary" onClick={resetFilters}>
            {i18n.t('ats.applications_table.filters.reset')}
          </Button>
          <Button onClick={onApplyFilters}>{i18n.t('ats.applications_table.filters.apply')}</Button>
        </Box>
      </Box>
    </Popover>
  )
}
